import styled from "styled-components";
import { MEDIA_MOBILE_BREAKPOINT } from "src/apps/config/constants";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { OrderBox } from "../components/orderBox/OrderBox";
import { OrderModel } from "src/contexts/documentationArea/domain/OrderModel";
import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { OpenAdminBanner } from "src/contexts/documentationArea/domain/OpenAdminBanner";
import { OpenAdminBannerByStatus } from "../components/openAdminBanner";
import { useEffect, useState } from "react";
import { OpenAdminWidgetEventNames } from "src/contexts/documentationArea/application/OpenAdminWidgetEventNames";
import {
  AnalyticsEvents,
  useSendAnalyticEvent,
} from "src/apps/hooks/analytics/useSendAnalyticEvent";
import { useCreateOpenAdminSession } from "src/apps/hooks/useCreateOpenAdminSession";
import { useQueryClient } from "@tanstack/react-query";
import { useDocumentationAreaIdAndActorId } from "src/apps/hooks/useDocumentationAreaIdAndActorId";

const ADMIN_WINDOW_NAME = "documentationAreaWindow";

interface ModelsContentProps {
  models: OrderModel[];
}

export const ModelsContent = ({ models }: ModelsContentProps) => {
  const { isMobile } = useIsMedia();
  const { openAdminBanner, isAnyModelOrderProcessing } = useDocumentationArea();
  const [openTab, setOpenTab] = useState<Window | null>(null);
  const [widgetLink, setWidgetLink] = useState<string | null>(null);
  const { sendEvent } = useSendAnalyticEvent();
  const { createOpenAdminSession, isPending: isCreatingSession } = useCreateOpenAdminSession();
  const queryClient = useQueryClient();
  const { documentationAreaId } = useDocumentationAreaIdAndActorId();

  const bannerState = openAdminBanner?.getStatus() ?? "not_specified";

  const bannerAnalyticsProperties = {
    open_admin_banner_state: bannerState,
  };

  useEffect(() => {
    if (openTab && widgetLink) {
      openTab.location.href = widgetLink;
      const onMessage = (event: MessageEvent) => {
        if (event.data?.name === OpenAdminWidgetEventNames.END_FLOW_BASE_SCREEN) {
          openTab?.close();
          setOpenTab(null);
          setWidgetLink(null);
        }
      };
      window.addEventListener("message", onMessage);
      return () => {
        window.removeEventListener("message", onMessage);
      };
    }
  }, [widgetLink, openTab]);

  const handleOpenAdminClick = (banner: OpenAdminBanner) => {
    const sessionLink = banner.getSessionLink();
    sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_OPEN_ADMIN_CLICKED, {
      ...bannerAnalyticsProperties,
    });
    const openedTab = window.open("", ADMIN_WINDOW_NAME);

    if (!openedTab) return;

    setOpenTab(openedTab);

    const interval = setInterval(() => {
      if (openedTab?.closed) {
        clearInterval(interval);
        setOpenTab(null);
        setWidgetLink(null);
      }
    }, 500);

    if (!sessionLink) {
      createOpenAdminSession(undefined, {
        onSuccess: (data) => {
          setWidgetLink(data.widgetLink);
          queryClient.invalidateQueries({ queryKey: ["DocumentationArea", documentationAreaId] });

          sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_OPEN_ADMIN_CLICKED, {
            ...bannerAnalyticsProperties,
            session_created: true,
          });
        },
        onError: () => {
          // TODO: show a toast with an error
        },
      });
      return;
    }

    setWidgetLink(sessionLink);
  };

  const renderOpenAdminBanner = (banner: OpenAdminBanner) => {
    return (
      <OpenAdminBannerByStatus
        status={banner.getStatus()}
        title={banner.getTitle()}
        description={banner.getSubtitle()}
        buttonText={banner.getButtonText()}
        onButtonClick={() => handleOpenAdminClick(banner)}
        isDisabled={isAnyModelOrderProcessing}
        isLoading={isCreatingSession}
      />
    );
  };

  return (
    <Container $isMobile={isMobile}>
      {openAdminBanner ? renderOpenAdminBanner(openAdminBanner) : null}
      {models.map((order) => {
        return (
          <OrderBoxContainer key={order.getId()}>
            <OrderBox order={order} />
          </OrderBoxContainer>
        );
      })}
    </Container>
  );
};

const OrderBoxContainer = styled.div`
  align-self: stretch;
`;

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.75rem;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    padding: 0rem;
    padding-top: 1rem;
    gap: 1rem;
  }
  align-items: stretch;
`;
